import { differenceInDays, differenceInHours, differenceInMonths, isPast } from "date-fns";

// Helpers
import {
    MAX_POSTPONE_COUNT,
    ONBOARDING_PHASE_DAYS,
    ONBOARDING_PHASE_MONTHS,
    THERAPY_PROGRESS_PHASE_MONTHS
} from "@/helpers/constants";

// Types
import { Feedback, UserEvent } from "@/lib/types/types";

/**
 * Determines whether a user should see the feedback modal based on their therapy journey phase and feedback history.
 *
 * Therapy Journey Phases:
 * 1. Onboarding Phase (0-90 days from therapy start)
 *    - Triggered after completing 4 therapy sessions
 *    - One-time feedback collection during this phase
 *    - Can be postponed up to 3 times
 *
 * 2. Therapy Progress Phases (6+ months from therapy start)
 *    - Occurs every 6 months (6-12 months, 12-18 months, etc.)
 *    - Each 6-month period is considered a separate phase
 *    - Each phase allows up to 3 postponements
 *    - Postpone count resets when entering a new 6-month period
 *
 * Postponement Logic:
 * - Users can postpone feedback up to 3 times per phase
 * - Postponing adds a 48-hour delay before showing the modal again
 * - Postpone count is phase-specific and resets when:
 *   a) Moving from onboarding to therapy progress
 *   b) Entering a new 6-month period in therapy progress
 * - If max postponements (3) reached, modal won't show until next phase
 *
 * Phase Period Calculation:
 * - Uses Math.floor(monthsSinceStart / 6) to determine current period
 * - Period 0: Onboarding phase
 * - Period 1: First therapy progress phase (6-12 months)
 * - Period 2: Second therapy progress phase (12-18 months)
 * - And so on...
 *
 * Example Timeline:
 * - Month 0-3: Onboarding phase (after 4 sessions)
 * - Month 6-12: First therapy progress phase
 * - Month 12-18: Second therapy progress phase
 *
 */
export const isEligibleForFeedback = ({
    events,
    feedback,
    therapyStartDate,
    shouldConsiderPostponed = true
}: {
    events: UserEvent[];
    feedback: Feedback | undefined;
    therapyStartDate: string | undefined;
    shouldConsiderPostponed?: boolean;
}) => {
    if (!therapyStartDate) return false;

    const today = new Date();
    const startDate = new Date(therapyStartDate);

    const isOnboardingPhase = differenceInDays(today, startDate) <= ONBOARDING_PHASE_DAYS;
    const isTherapyProgressPhase = differenceInMonths(today, startDate) >= THERAPY_PROGRESS_PHASE_MONTHS;

    const hasPostponedFeedback =
        shouldConsiderPostponed && feedback?.postponed_at
            ? differenceInHours(today, new Date(feedback.postponed_at)) < 48
            : false;

    // Calculate current phase and period
    const monthsSinceStart = differenceInMonths(today, startDate);
    const currentPeriodNumber = isTherapyProgressPhase
        ? Math.floor(monthsSinceStart / THERAPY_PROGRESS_PHASE_MONTHS)
        : 0; // 0 represents onboarding phase

    const lastPostponementDate = feedback?.postponed_at ? new Date(feedback.postponed_at) : null;

    const monthsSinceStartDateLastPostponement = lastPostponementDate
        ? differenceInMonths(lastPostponementDate, startDate)
        : null;

    const monthsSinceLastPostponement = monthsSinceStartDateLastPostponement
        ? monthsSinceStartDateLastPostponement < 6 && monthsSinceStartDateLastPostponement > 3
            ? 6
            : monthsSinceStartDateLastPostponement
        : null;

    const lastPostponementPeriodNumber = lastPostponementDate
        ? differenceInMonths(lastPostponementDate, startDate) <= ONBOARDING_PHASE_MONTHS
            ? 0 // onboarding phase
            : monthsSinceLastPostponement
            ? Math.floor(monthsSinceLastPostponement / THERAPY_PROGRESS_PHASE_MONTHS)
            : null
        : null;

    // Reset count if we're in a new period
    const isNewPhase = lastPostponementPeriodNumber === null || currentPeriodNumber !== lastPostponementPeriodNumber;
    const currentPhasePostponeCount = isNewPhase ? 0 : feedback?.postpone_count ?? 0;

    if (isOnboardingPhase) {
        const pastEvents = events.filter(event => event.type === "session" && isPast(event.start));
        const isEligibleForOnboardingFeedback =
            pastEvents.length >= 4 && !feedback?.last_feedback_at && currentPhasePostponeCount < MAX_POSTPONE_COUNT;

        if (isEligibleForOnboardingFeedback && !hasPostponedFeedback) {
            return true;
        }
    }

    if (isTherapyProgressPhase) {
        if (!feedback?.last_feedback_at) {
            return !hasPostponedFeedback && currentPhasePostponeCount < MAX_POSTPONE_COUNT;
        }

        const isOnboardingFeedback =
            differenceInMonths(new Date(feedback.last_feedback_at), startDate) <= ONBOARDING_PHASE_MONTHS;

        if (isOnboardingFeedback && !hasPostponedFeedback && currentPhasePostponeCount < MAX_POSTPONE_COUNT) {
            return true;
        }

        const hasPassed6MonthsSinceLastFeedback =
            feedback?.last_feedback_at &&
            differenceInMonths(today, new Date(feedback.last_feedback_at)) >= THERAPY_PROGRESS_PHASE_MONTHS;

        if (
            hasPassed6MonthsSinceLastFeedback &&
            !hasPostponedFeedback &&
            currentPhasePostponeCount < MAX_POSTPONE_COUNT
        ) {
            return true;
        }
    }

    return false;
};
